// ---------------------------------------------------------------------------
// C9: Agency Directory — /marketplace/directory
// ---------------------------------------------------------------------------
// Searchable directory. Profile cards. Certification badges. Filters.
// ---------------------------------------------------------------------------
import { useState } from "react";
import {
  Search,
  MapPin,
  Star,
  Users,
  Award,
  ExternalLink,
  Filter,
  AlertCircle,
  Building2,
} from "lucide-react";
import { useAgencyDirectory, type AgencyProfile } from "@/hooks/use-marketplace";
import { useDebounce } from "@/hooks/use-debounce";
import { EmptyState } from "@/components/ui/empty-state";
import { Skeleton, SkeletonCard } from "@/components/skeleton";
import { formatCurrency } from "@/lib/i18n";

// ---------------------------------------------------------------------------
// Skeleton
// ---------------------------------------------------------------------------

function DirectorySkeleton() {
  return (
    <div className="space-y-6">
      <Skeleton className="h-8 w-64" />
      <Skeleton className="h-10 rounded-lg" />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 6 }, (_, i) => (
          <SkeletonCard key={i} className="h-72" />
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Certification Badge
// ---------------------------------------------------------------------------

const CERT_COLORS = {
  bronze: "bg-[var(--color-warning)] text-white",
  silver: "bg-[var(--color-muted-foreground)] text-white",
  gold: "bg-[var(--color-warning)]/40 text-[var(--color-warning)]",
  platinum: "bg-slate-200 text-slate-800",
} as const;

function CertBadge({ cert }: { cert: { name: string; level: "bronze" | "silver" | "gold" | "platinum" } }) {
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${CERT_COLORS[cert.level]}`}>
      <Award className="h-3 w-3" />
      {cert.name}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Agency Card
// ---------------------------------------------------------------------------

function AgencyCard({ agency }: { agency: AgencyProfile }) {
  const ratingLabel = agency.reviewCount > 0
    ? `${agency.rating.toFixed(1)} (${agency.reviewCount})`
    : agency.rating > 0
      ? `${agency.rating.toFixed(1)} avg rating`
      : "No rating yet";
  const managedRevenueLabel = agency.verifiedPerformance.totalManagedRevenueCents > 0
    ? formatCurrency(agency.verifiedPerformance.totalManagedRevenueCents, "EUR", { maximumFractionDigits: 0 })
    : null;

  return (
    <div className={`stagger-item overflow-hidden rounded-lg border bg-background ${agency.featured ? "border-[var(--color-primary)] ring-1 ring-[var(--color-primary)]/20" : "border-border"}`}>
      {agency.featured && (
        <div className="bg-[var(--color-primary)] px-3 py-1 text-center text-xs font-medium text-white">
          Featured Partner
        </div>
      )}

      <div className="p-4">
        {/* Header */}
        <div className="flex items-start gap-3">
          <div className="h-12 w-12 overflow-hidden rounded-lg bg-[var(--color-primary)]/10 flex items-center justify-center flex-shrink-0">
            {agency.logoUrl ? (
              <img
                src={agency.logoUrl}
                alt={`${agency.name} logo`}
                className="h-full w-full object-cover"
                loading="lazy"
              />
            ) : (
              <Building2 className="h-6 w-6 text-[var(--color-primary)]" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-medium text-foreground truncate">{agency.name}</h3>
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <MapPin className="h-3 w-3" />
              {agency.location}
            </div>
          </div>
        </div>

        <p className="mt-3 text-xs text-muted-foreground line-clamp-2">{agency.description}</p>

        {/* Specialties */}
        <div className="mt-3 flex flex-wrap gap-1">
          {agency.verticals.slice(0, 2).map((vertical) => (
            <span key={vertical} className="rounded-full border border-[var(--color-primary)]/30 bg-[var(--color-primary)]/10 px-2 py-0.5 text-[10px] text-[var(--color-primary)]">
              {vertical}
            </span>
          ))}
          {agency.specialties.slice(0, 4).map((spec) => (
            <span key={spec} className="rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground">
              {spec}
            </span>
          ))}
          {agency.specialties.length > 4 && (
            <span className="text-[10px] text-muted-foreground">+{agency.specialties.length - 4} more</span>
          )}
        </div>

        {/* Certifications */}
        {agency.certifications.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {agency.certifications.map((cert) => (
              <CertBadge key={cert.name} cert={cert} />
            ))}
          </div>
        )}

        {agency.verifiedPerformance.isVerified && (
          <div className="mt-3 rounded-lg border border-[var(--color-primary)]/15 bg-[var(--color-primary)]/5 p-2">
            <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--color-primary)]">
              Verified Analytics
            </div>
            <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
              <span>{agency.clientCount} managed clients</span>
              {managedRevenueLabel && (
                <span>{managedRevenueLabel} managed revenue</span>
              )}
              {agency.verifiedPerformance.avgClientRetentionMonths !== null && (
                <span>{agency.verifiedPerformance.avgClientRetentionMonths} mo avg retention</span>
              )}
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="mt-3 flex items-center justify-between border-t border-border pt-3">
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <span className="flex items-center gap-0.5">
              <Star className="h-3 w-3 fill-amber-400 text-[var(--color-warning)]/80" />
              {ratingLabel}
            </span>
            <span className="flex items-center gap-0.5">
              <Users className="h-3 w-3" />
              {agency.clientCount} clients
            </span>
          </div>
          {agency.contactUrl ? (
            <a
              href={agency.contactUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 rounded-lg bg-[var(--color-primary)] px-3 py-1.5 text-xs font-medium text-white hover:bg-[var(--color-primary)]/90 transition-colors"
            >
              Contact
              <ExternalLink className="h-3 w-3" />
            </a>
          ) : (
            <span className="text-[10px] text-muted-foreground">Direct contact unavailable</span>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export default function AgencyDirectory() {
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebounce(searchQuery, 300);
  const [selectedVertical, setSelectedVertical] = useState<string | undefined>(undefined);
  const [selectedLocation, setSelectedLocation] = useState<string | undefined>(undefined);
  const [selectedSpecialty, setSelectedSpecialty] = useState<string | undefined>(undefined);
  const { agencies, isLoading } = useAgencyDirectory(selectedVertical, debouncedSearch || undefined);
  const data = agencies.length > 0
    ? {
      agencies: agencies.filter((agency) => {
        if (selectedLocation && agency.location !== selectedLocation) {
          return false;
        }

        if (selectedSpecialty && !agency.specialties.includes(selectedSpecialty)) {
          return false;
        }

        return true;
      }),
      total: agencies.length,
      specialties: [...new Set(agencies.flatMap((agency) => agency.specialties))]
        .sort((left, right) => left.localeCompare(right)),
      verticals: [...new Set(agencies.flatMap((agency) => agency.verticals))]
        .sort((left, right) => left.localeCompare(right)),
      locations: [...new Set(agencies.map((agency) => agency.location))]
        .sort((left, right) => left.localeCompare(right)),
    }
    : null;
  const error = null;

  if (isLoading) return <DirectorySkeleton />;

  if (error) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 p-4 text-sm text-[var(--color-destructive)]">
        <AlertCircle className="h-4 w-4" />
        Failed to load agency directory.
      </div>
    );
  }

  if (!data || data.agencies.length === 0) {
    return (
      <EmptyState
        icon={<Building2 className="h-8 w-8" />}
        title="No agencies listed yet"
        description="The agency directory will grow as certified partners join the platform."
        variant="info"
      />
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-foreground">Agency Directory</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Find certified partners to help grow your business.
        </p>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search agencies by name, specialty, or location..."
          className="w-full rounded-lg border border-border bg-background pl-10 pr-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
        />
      </div>

      {/* Vertical Filters */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1">
        <Filter className="h-4 w-4 text-muted-foreground flex-shrink-0" />
        <button
          onClick={() => setSelectedVertical(undefined)}
          className={`whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium transition-colors ${
            !selectedVertical
              ? "bg-[var(--color-primary)] text-white"
              : "bg-muted text-muted-foreground hover:text-foreground"
          }`}
        >
          All Verticals
        </button>
        {data.verticals.map((v) => (
          <button
            key={v}
            onClick={() => setSelectedVertical(v)}
            className={`whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium transition-colors ${
              selectedVertical === v
                ? "bg-[var(--color-primary)] text-white"
                : "bg-muted text-muted-foreground hover:text-foreground"
            }`}
          >
            {v}
          </button>
        ))}
      </div>

      {/* Specialty Filters */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1">
        <Award className="h-4 w-4 text-muted-foreground flex-shrink-0" />
        <button
          onClick={() => setSelectedSpecialty(undefined)}
          className={`whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium transition-colors ${
            !selectedSpecialty
              ? "bg-[var(--color-primary)] text-white"
              : "bg-muted text-muted-foreground hover:text-foreground"
          }`}
        >
          All Specialties
        </button>
        {data.specialties.map((specialty) => (
          <button
            key={specialty}
            onClick={() => setSelectedSpecialty(specialty)}
            className={`whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium transition-colors ${
              selectedSpecialty === specialty
                ? "bg-[var(--color-primary)] text-white"
                : "bg-muted text-muted-foreground hover:text-foreground"
            }`}
          >
            {specialty}
          </button>
        ))}
      </div>

      {/* Location Filters */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1">
        <MapPin className="h-4 w-4 text-muted-foreground flex-shrink-0" />
        <button
          onClick={() => setSelectedLocation(undefined)}
          className={`whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium transition-colors ${
            !selectedLocation
              ? "bg-[var(--color-primary)] text-white"
              : "bg-muted text-muted-foreground hover:text-foreground"
          }`}
        >
          All Locations
        </button>
        {data.locations.map((location) => (
          <button
            key={location}
            onClick={() => setSelectedLocation(location)}
            className={`whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium transition-colors ${
              selectedLocation === location
                ? "bg-[var(--color-primary)] text-white"
                : "bg-muted text-muted-foreground hover:text-foreground"
            }`}
          >
            {location}
          </button>
        ))}
      </div>

      {/* Results count */}
      <p className="text-sm text-muted-foreground">{data.agencies.length} agencies found</p>

      {/* Agency Grid */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {data.agencies.map((agency) => (
          <AgencyCard key={agency.id} agency={agency} />
        ))}
      </div>
    </div>
  );
}
